/**
 * SPIN TENET — Run Execution & Lifecycle Manager (Gate 3 Implementation)
 *
 * Coordinates:
 * - Immutable Run Specification submission to Engine Adapter boundary.
 * - Periodic lifecycle polling (QUEUED -> PREPARING -> RUNNING -> COMPLETED / FAILED).
 * - Normalized CfdResult retrieval upon completion.
 * - Dispatches updates strictly into Redux state without storing raw buffers.
 */

import type { AppDispatch } from '@state/store';
import { registerRun, updateRunStatus } from '@state/slices/runSlice';
import type {
  RunSpecification,
  CfdResultSummary,
  RunLifecycleStatus
} from '@contracts/index';
import {
  validateCfdResultIntegrity,
  deriveVelocityMagnitudeField
} from '@contracts/index';
import { EngineRegistry } from './engineRegistry';

const activePollers = new Map<string, number>();

export class RunExecutionManager {
  /**
   * Submits an immutable Run Specification through the Engine Adapter boundary
   * and tracks its execution lifecycle until terminal state.
   */
  public static async executeRun(
    specification: Readonly<RunSpecification>,
    dispatch: AppDispatch
  ): Promise<void> {
    const { runId, engine } = specification;

    // 1. Initial Redux registration in QUEUED state
    dispatch(registerRun({ specification, initialStatus: 'QUEUED' }));

    const adapter = EngineRegistry.getAdapter(engine);
    if (!adapter) {
      dispatch(
        updateRunStatus({
          runId,
          status: 'FAILED',
          error: `Engine '${engine}' is not registered in the EngineRegistry.`
        })
      );
      return;
    }

    try {
      // 2. Prepare payload
      await adapter.prepare(specification);

      // 3. Submit to remote execution plane
      const submission = await adapter.submit(specification);
      if (!submission.success) {
        dispatch(
          updateRunStatus({
            runId,
            status: 'FAILED',
            error: submission.message
          })
        );
        return;
      }

      dispatch(
        updateRunStatus({
          runId,
          status: submission.state,
          remoteRunId: submission.remoteRunId
        })
      );

      // 4. Start polling loop
      this.pollRunStatus(runId, engine, dispatch);
    } catch (err: any) {
      dispatch(
        updateRunStatus({
          runId,
          status: 'FAILED',
          error: `Execution error: ${err.message || String(err)}`
        })
      );
    }
  }

  /**
   * Periodically polls run lifecycle status and extracts authoritative results.
   */
  public static pollRunStatus(
    runId: string,
    engine: import('@contracts/index').EngineId,
    dispatch: AppDispatch
  ): void {
    if (activePollers.has(runId)) {
      clearInterval(activePollers.get(runId));
      activePollers.delete(runId);
    }

    const adapter = EngineRegistry.getAdapter(engine);
    if (!adapter) {
      dispatch(
        updateRunStatus({
          runId,
          status: 'FAILED',
          error: `Engine '${engine}' is not registered in the EngineRegistry.`
        })
      );
      return;
    }

    const intervalId = window.setInterval(async () => {
      try {
        const status: RunLifecycleStatus = await adapter.getRunStatus(runId);

        if (status === 'COMPLETED') {
          window.clearInterval(intervalId);
          activePollers.delete(runId);

          let result: import('@contracts/index').CfdResult | null = null;
          try {
            result = await adapter.getResult(runId);
          } catch (fetchErr: any) {
            dispatch(
              updateRunStatus({
                runId,
                status: 'FAILED',
                error: `RESULT_NOT_AVAILABLE: Network error retrieving result: ${fetchErr.message || String(fetchErr)}`
              })
            );
            return;
          }

          if (!result) {
            dispatch(
              updateRunStatus({
                runId,
                status: 'FAILED',
                error: 'RESULT_NOT_AVAILABLE: Remote solver completed but returned no CfdResult.'
              })
            );
            return;
          }

          // Run ID mismatch rejection
          if (result.runId !== runId) {
            dispatch(
              updateRunStatus({
                runId,
                status: 'FAILED',
                error: `RUN_NOT_FOUND: Returned result runId (${result.runId}) does not match requested run (${runId}).`
              })
            );
            return;
          }

          // Engine mismatch rejection
          if (result.engine !== engine) {
            dispatch(
              updateRunStatus({
                runId,
                status: 'FAILED',
                error: `ENGINE_MISMATCH: Returned result engine (${result.engine}) does not match requested engine (${engine}).`
              })
            );
            return;
          }

          // G3.1 Integrity validation
          const validation = validateCfdResultIntegrity(result);
          if (!validation.isValid) {
            dispatch(
              updateRunStatus({
                runId,
                status: 'FAILED',
                error: `RESULT_INVALID: Result integrity validation failed: ${validation.errors.join('; ')}`
              })
            );
            return;
          }

          // Store ONLY result metadata in Redux (zero large numerical arrays)
          const vel = result.fields?.velocity;
          let minSpeed: number | undefined;
          let maxSpeed: number | undefined;
          let meanSpeed: number | undefined;

          if (result.fieldOrigin === 'AUTHORITATIVE_CFD') {
            try {
              const derived = deriveVelocityMagnitudeField(result);
              minSpeed = derived.minMagnitudeMps;
              maxSpeed = derived.maxMagnitudeMps;
              meanSpeed = derived.meanMagnitudeMps;
            } catch (err) {
              console.warn('[RunExecutionManager] Could not derive magnitude summary:', err);
            }
          }

          const summary: CfdResultSummary = {
            resultVersion: result.resultVersion,
            runId: result.runId,
            engine: result.engine,
            engineVersion: result.engineVersion,
            status: 'COMPLETED',
            fieldOrigin: result.fieldOrigin,
            coordinateSystem: result.coordinateSystem,
            pointCount: vel?.count || 0,
            minSpeed,
            maxSpeed,
            meanSpeed,
            dataLocation: vel?.dataLocation || 'CELL_CENTERS',
            completedAt: result.provenance?.executedAt || new Date().toISOString(),
            provenanceSummary: result.provenance?.notes || undefined
          };

          dispatch(updateRunStatus({ runId, status: 'COMPLETED', resultSummary: summary }));
        } else if (status === 'FAILED' || status === 'CANCELLED') {
          window.clearInterval(intervalId);
          activePollers.delete(runId);

          let errDetail = 'CFD solver computation failed or was cancelled.';
          if ('getRunError' in adapter && typeof (adapter as any).getRunError === 'function') {
            try {
              const remoteErr = await (adapter as any).getRunError(runId);
              if (remoteErr) errDetail = remoteErr;
            } catch {
              // Ignore
            }
          }

          dispatch(
            updateRunStatus({
              runId,
              status,
              error: errDetail
            })
          );
        } else {
          dispatch(updateRunStatus({ runId, status }));
        }
      } catch (err: any) {
        console.warn(`[RunExecutionManager] Polling error for ${runId}:`, err);
      }
    }, 1500);

    activePollers.set(runId, intervalId);
  }

  public static stopPolling(runId: string): void {
    if (activePollers.has(runId)) {
      window.clearInterval(activePollers.get(runId));
      activePollers.delete(runId);
    }
  }
}
